
import { t } from '../i18n';

type Props = {
    participant: {
        userId: string;
        name: string;
        status: string;
        hasSelected: boolean;
        value: string | null;
    };
    isRevealed: boolean;
    isMe: boolean;
};

// French playing card suits (round-robin assignment)
const SUITS = [
    { symbol: '♠', color: 'text-slate-800', name: 'Pik' },      // Spades - black
    { symbol: '♥', color: 'text-red-600', name: 'Herz' },       // Hearts - red
    { symbol: '♣', color: 'text-slate-800', name: 'Kreuz' },    // Clubs - black
    { symbol: '♦', color: 'text-red-600', name: 'Karo' }        // Diamonds - red
];

export default function FlipCard({ participant, isRevealed, isMe }: Readonly<Props>) {
    const { name, status, hasSelected, value } = participant;

    // Assign suit based on userId (consistent per user)
    const suitIndex = Math.abs(participant.userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % 4;
    const suit = SUITS[suitIndex];

    return (
        <div className="flex flex-col items-center gap-3">
            {/* Name Header */}
            <div className="w-full text-center px-2">
                <div className="font-medium truncate text-sm flip-card-name" title={name}>
                    {name} {status === 'away' ? <span className="text-xs text-slate-400">{t('flip.away')}</span> : null}
                </div>
                <div className="text-xs text-slate-500 mt-1 flip-card-status">
                    {hasSelected ? t('flip.selected') : '…'}
                </div>
            </div>

            {/* Playing Card (Portrait) */}
            <div className={[
                'relative w-32 h-48 rounded-lg shadow-xl border transition-all duration-300',
                isMe && hasSelected ? 'border-green-500 ring-2 ring-green-300' : '',
                isMe && !hasSelected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-slate-200',
                !isRevealed ? 'transform hover:scale-105' : ''
            ].join(' ')}>
                {isRevealed && hasSelected ? (
                    // Front: Classic French playing card style with assigned suit
                    <div className="w-full h-full bg-white rounded-lg relative flex items-center justify-center border-4 border-slate-100">
                        {/* Top-left corner - value with suit */}
                        <div className="absolute top-2 left-2 flex flex-col items-center ">
                            <span className={`text-sm font-bold ${suit.color}`}>
                                {value ?? '?'}
                            </span>
                            <span className={`text-xs ${suit.color}`}>
                                {suit.symbol}
                            </span>
                        </div>

                        {/* Top-right corner - value with suit */}
                        <div className="absolute top-2 right-2 flex flex-col items-center ">
                            <span className={`text-sm font-bold ${suit.color}`}>
                                {value ?? '?'}
                            </span>
                            <span className={`text-xs ${suit.color}`}>
                                {suit.symbol}
                            </span>
                        </div>

                        {/* Bottom-left corner - value with suit (upside down) */}
                        <div className="absolute bottom-2 left-2 flex flex-col items-center -rotate-[180deg]">
                            <span className={`text-sm font-bold ${suit.color}`}>
                                {value ?? '?'}
                            </span>
                            <span className={`text-xs ${suit.color}`}>
                                {suit.symbol}
                            </span>
                        </div>

                        {/* Bottom-right corner - value with suit (upside down) */}
                        <div className="absolute bottom-2 right-2 flex flex-col items-center rotate-[180deg]">
                            <span className={`text-sm font-bold ${suit.color}`}>
                                {value ?? '?'}
                            </span>
                            <span className={`text-xs ${suit.color}`}>
                                {suit.symbol}
                            </span>
                        </div>

                        {/* Center - large value with suit */}
                        <div className="flex flex-col items-center gap-1">
                            <div className={`text-6xl font-bold ${suit.color}`}>
                                {value ?? '—'}
                            </div>
                            <div className={`text-4xl ${suit.color}`}>
                                {suit.symbol}
                            </div>
                        </div>
                    </div>
                ) : (
                    // Back: Classic French card back with ornate pattern
                    <div className="w-full h-full rounded-lg relative overflow-hidden">
                        {/* Classic blue/red pattern background */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-200 to-blue-900"></div>

                        {/* Ornate border pattern */}
                        <div className="absolute inset-0 border-8 border-double border-yellow-400/40 rounded-lg"></div>
                        <div className="absolute inset-2 border-4 border-red-600/30 rounded-md"></div>

                        {/* Corner decorations (like classic cards) */}
                        <div className="absolute inset-0">
                            <span className="absolute top-3 left-3 text-slate-800/80 text-2xl">♠</span>
                            <span className="absolute top-3 right-3 text-red-500/80 text-2xl">♥</span>
                            <span className="absolute bottom-3 left-3 text-red-500/80 text-2xl">♦</span>
                            <span className="absolute bottom-3 right-3 text-slate-800/80 text-2xl">♣</span>
                        </div>

                        {/* Center ornamental pattern */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="relative w-20 h-20">
                                {/* Decorative circular pattern */}
                                <div className="absolute inset-0 rounded-full border-4 border-yellow-400/30 flex items-center justify-center">
                                    <div className="w-16 h-16 rounded-full border-2 border-red-600/40 flex items-center justify-center bg-blue-900/90">
                                        <img
                                            src="/card_logo_wh.svg"
                                            alt="Logo"
                                            className="w-14 h-14 object-contain opacity-90 drop-shadow-lg"
                                        />
                                    </div>
                                </div>

                                {/* Additional decorative elements */}
                                <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-slate-800/80 text-xs">♠</div>
                                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-red-500/80 text-xs">♥</div>
                                <div className="absolute top-1/2 -left-2 -translate-y-1/2 text-red-500/80 text-xs">♦</div>
                                <div className="absolute top-1/2 -right-2 -translate-y-1/2 text-slate-800/80 text-xs">♣</div>
                            </div>
                        </div>

                        {/* Subtle pattern overlay */}
                        <div className="absolute inset-0 opacity-5">
                            <div className="grid grid-cols-3 grid-rows-5 h-full w-full">
                                {[...Array(15)].map((_, i) => (
                                    <div key={i} className="flex items-center justify-center text-white text-xs">
                                        {i % 4 === 0 ? '♠' : i % 4 === 1 ? '♥' : i % 4 === 2 ? '♦' : '♣'}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
